import { useEffect, useRef, useCallback } from 'react'

interface UseAutoLogoutOptions {
  logout: () => void
  isAuthenticated: boolean
  timeout?: number // Timeout in milliseconds
  onWarning?: (remainingSeconds: number) => void
  warningTime?: number // Time in milliseconds before logout to show warning
}

/**
 * Hook that automatically logs out users after a period of inactivity.
 * Tracks mouse movements, keyboard events, and clicks to reset the timer.
 *
 * @param options Configuration options
 * @param options.logout - Logout function to call when timeout expires
 * @param options.isAuthenticated - Whether user is currently authenticated
 * @param options.timeout - Inactivity timeout in milliseconds (default: from env or 15 minutes)
 * @param options.onWarning - Callback when warning should be shown
 * @param options.warningTime - Time before logout to trigger warning (default: 60 seconds)
 */
export const useAutoLogout = (options: UseAutoLogoutOptions) => {
  const { logout, isAuthenticated } = options

  // Get timeout from options, env, or default to 15 minutes
  const defaultTimeout = import.meta.env.VITE_AUTO_LOGOUT_TIMEOUT
    ? parseInt(import.meta.env.VITE_AUTO_LOGOUT_TIMEOUT)
    : 15 * 60 * 1000

  const timeout = options.timeout ?? defaultTimeout
  const warningTime = options.warningTime ?? 60 * 1000 // 60 seconds default

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastActivityRef = useRef<number>(Date.now())

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current)
      warningTimeoutRef.current = null
    }
  }, [])

  const handleLogout = useCallback(() => {
    clearTimers()
    logout()
  }, [logout, clearTimers])

  const resetTimer = useCallback(() => {
    clearTimers()
    lastActivityRef.current = Date.now()

    // Set warning timer
    if (options.onWarning && warningTime < timeout) {
      warningTimeoutRef.current = setTimeout(() => {
        const remainingSeconds = Math.ceil(
          (timeout - (Date.now() - lastActivityRef.current)) / 1000,
        )
        options.onWarning?.(remainingSeconds)
      }, timeout - warningTime)
    }

    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      handleLogout()
    }, timeout)
  }, [timeout, warningTime, options, handleLogout, clearTimers])

  useEffect(() => {
    // Only set up auto-logout if user is authenticated
    if (!isAuthenticated) {
      clearTimers()
      return
    }

    // Events to track user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ]

    // Throttle the reset to avoid too many timer resets
    let throttleTimeout: NodeJS.Timeout | null = null
    const throttledResetTimer = () => {
      if (!throttleTimeout) {
        throttleTimeout = setTimeout(() => {
          resetTimer()
          throttleTimeout = null
        }, 1000) // Throttle to once per second
      }
    }

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, throttledResetTimer)
    })

    // Initialize timer
    resetTimer()

    // Cleanup
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, throttledResetTimer)
      })
      clearTimers()
      if (throttleTimeout) {
        clearTimeout(throttleTimeout)
      }
    }
  }, [isAuthenticated, resetTimer, clearTimers])

  return {
    resetTimer,
    clearTimers,
  }
}
